import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { 
  Plus, Trash2, Search, UserMinus, Edit, Phone, Users 
} from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const SubAdminsManagement = () => {
  const { user } = useAuth();
  const [subAdmins, setSubAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);
  const [deleteAdmin, setDeleteAdmin] = useState(null);
  const [newAdmin, setNewAdmin] = useState({ name: "", phone: "", email: "" });

  useEffect(() => { 
    if (user?.id) {
      fetchSubAdmins(); 
    }
  }, [user?.id]);

  const fetchSubAdmins = async () => {
    try {
      const res = await axios.get(`${API}/admin/${user.id}/sub-admins`);
      setSubAdmins(res.data);
    } catch (err) {
      toast.error("Failed to load sub-admins");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newAdmin.name || !newAdmin.phone) {
      toast.error("Name and phone are required");
      return;
    }
    if (newAdmin.phone.length !== 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    try {
      await axios.post(`${API}/admin/${user.id}/sub-admins`, newAdmin);
      toast.success("Sub-Admin created successfully");
      setShowAddDialog(false);
      setNewAdmin({ name: "", phone: "", email: "" });
      fetchSubAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create sub-admin");
    }
  };

  const handleUpdate = async () => {
    if (!editAdmin) return;
    try {
      await axios.put(`${API}/admin/${user.id}/sub-admins/${editAdmin.id}`, {
        name: editAdmin.name,
        phone: editAdmin.phone,
        email: editAdmin.email,
      });
      toast.success("Sub-Admin updated");
      setEditAdmin(null);
      fetchSubAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const handleToggleStatus = async (admin) => {
    try {
      const newStatus = !admin.is_active;
      await axios.put(`${API}/admin/${user.id}/sub-admins/${admin.id}/status?is_active=${newStatus}`);
      toast.success(`Sub-Admin ${newStatus ? 'enabled' : 'disabled'}`);
      fetchSubAdmins();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async () => {
    if (!deleteAdmin) return;
    try {
      await axios.delete(`${API}/admin/${user.id}/sub-admins/${deleteAdmin.id}`);
      toast.success("Sub-Admin deleted");
      setDeleteAdmin(null);
      fetchSubAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const filteredAdmins = subAdmins.filter(admin =>
    admin.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.phone?.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="sub-admins-management">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Sub-Admins</h2>
          <p className="text-muted-foreground">
            {subAdmins.length} sub-admin{subAdmins.length !== 1 ? 's' : ''} in your account
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-sub-admin-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Sub-Admin
        </Button>
      </div>

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input 
          placeholder="Search by name, email, or phone..." 
          value={searchTerm} 
          onChange={(e) => setSearchTerm(e.target.value)} 
          className="pl-10" 
        />
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Users className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">Sub-Admin Permissions</p>
            <p className="text-sm text-blue-600">
              Sub-Admins have the same permissions as you, except they cannot delete your account or manage other sub-admins.
              They can log in using phone OTP.
            </p>
          </div>
        </div>
      </div>

      {/* Mobile Cards View */}
      <div className="md:hidden space-y-3">
        {filteredAdmins.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground bg-card rounded-xl border border-border">
            <UserMinus className="w-12 h-12 mx-auto mb-2 text-muted-foreground/50" />
            <p>No sub-admins yet</p>
            <p className="text-sm">Add your first sub-admin to delegate tasks</p>
          </div>
        ) : (
          filteredAdmins.map((admin) => (
            <div key={admin.id} className="bg-card rounded-xl border border-border p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <UserMinus className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">{admin.name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {admin.phone}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => setEditAdmin(admin)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setDeleteAdmin(admin)} className="text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                {admin.email ? (
                  <span className="text-xs text-muted-foreground">{admin.email}</span>
                ) : (
                  <span className="text-xs text-muted-foreground">No email</span>
                )}
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={admin.is_active !== false} 
                    onCheckedChange={() => handleToggleStatus(admin)} 
                  />
                  <span className={`text-xs ${admin.is_active !== false ? 'text-green-600' : 'text-red-600'}`}>
                    {admin.is_active !== false ? 'Active' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-card rounded-xl border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAdmins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  <UserMinus className="w-12 h-12 mx-auto mb-2 text-muted-foreground/50" />
                  <p>No sub-admins yet</p>
                  <p className="text-sm">Add your first sub-admin to delegate tasks</p>
                </TableCell>
              </TableRow>
            ) : (
              filteredAdmins.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <UserMinus className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="font-medium">{admin.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>{admin.phone}</TableCell>
                  <TableCell>{admin.email || <span className="text-muted-foreground">-</span>}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={admin.is_active !== false} 
                        onCheckedChange={() => handleToggleStatus(admin)} 
                      />
                      <span className={`text-sm ${admin.is_active !== false ? 'text-green-600' : 'text-red-600'}`}>
                        {admin.is_active !== false ? 'Active' : 'Disabled'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setEditAdmin(admin)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => setDeleteAdmin(admin)} 
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Sub-Admin</DialogTitle>
            <DialogDescription>
              Create a new sub-admin who can help manage your account
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input 
                value={newAdmin.name} 
                onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} 
                placeholder="Full Name" 
              />
            </div>
            <div>
              <Label>Phone Number *</Label>
              <Input 
                value={newAdmin.phone} 
                onChange={(e) => setNewAdmin({ ...newAdmin, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} 
                placeholder="10-digit phone number"
                maxLength={10}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Sub-admin will log in using this phone number with OTP
              </p>
            </div>
            <div>
              <Label>Email (Optional)</Label>
              <Input 
                type="email"
                value={newAdmin.email} 
                onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} 
                placeholder="email@example.com" 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate}>Create Sub-Admin</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editAdmin} onOpenChange={(open) => !open && setEditAdmin(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Sub-Admin</DialogTitle>
          </DialogHeader>
          {editAdmin && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Name</Label>
                <Input 
                  value={editAdmin.name} 
                  onChange={(e) => setEditAdmin({ ...editAdmin, name: e.target.value })} 
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input 
                  value={editAdmin.phone || ""} 
                  onChange={(e) => setEditAdmin({ ...editAdmin, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} 
                  maxLength={10}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={editAdmin.email || ""} 
                  onChange={(e) => setEditAdmin({ ...editAdmin, email: e.target.value })} 
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAdmin(null)}>Cancel</Button>
            <Button onClick={handleUpdate}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteAdmin} onOpenChange={() => setDeleteAdmin(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sub-Admin</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteAdmin?.name}</strong>? 
              They will no longer be able to access your account. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SubAdminsManagement;
